import { toastController } from '@/controllers/toastController'
import { uiController } from '@/controllers/uiController'
import { commonUtils } from '@/helpers/commonUtils'
import { envHelper } from '@/helpers/envHelper'
import { MODEL_STRATEGY_CONFIG } from '@/helpers/modelConsts'
import { themeHelper } from '@/helpers/themeHelper'
import { TranslateServiceType } from '@/services/translateService'
import { TranslateServiceConsts } from '@/services/translateService/consts'
import { userService } from '@/services/userService'
import { debounce, throttle } from 'lodash'
import { computed, makeObservable, observable } from 'mobx'
import { pdfProTranslateService } from '../PdfProTranslateService/PdfProTranslateService'
import { PdfProTranslateServiceType as ServiceType } from '../PdfProTranslateService/serviceTypes'
import { TRANSLATE_HELPER_CONFIG } from './config'
import { TranslateHelperConsts as Consts, TranslateHelperConsts } from './consts'
import { TranslateHelperTypes as Type } from './types'

type Config = Type.Config
type Paragraph = Type.Paragraph
type HTMLElementArray = Type.HTMLElementArray
export type RenderType = Type.RenderType
type TranslatableWrapper = Type.TranslatableWrapper

interface LangOption {
  from: string
  to: string
}

export class ProPdfTranslateHelper {
  @observable renderType: RenderType = 'original'
  @observable isBlockCurPage = false
  @observable detectLangInfo: { lang: string; name: string } | null = null

  @computed get actualApplySourceLang() {
    let lang = userService.pdfProSourceLang
    if (lang === TranslateServiceConsts.AUTO_DETECT_LANG && this.detectLangInfo) {
      lang = this.detectLangInfo.lang
    }
    return lang
  }

  public taskUid = commonUtils.genId()

  private audio: HTMLAudioElement | null = null

  private translatableParagraphs: Array<Paragraph> = []

  private intersectionObserverList: Array<IntersectionObserver> = []
  private intersectionObserver: IntersectionObserver | null = null

  private intersectionObserverElList: HTMLElementArray = []
  private intersectionObserverThrottleCall = debounce(
    async (triggerType: PostDataTypes.UseType) => {
      this.doTranslate(triggerType, this.intersectionObserverElList, this.renderType)
      this.intersectionObserverElList = []
      await this.updateUserInfoDebounceCall()
    },
    500,
    { leading: false, trailing: true }
  )
  private mutationObserver: MutationObserver | null = null
  private mutationObserverThrottleCall = throttle(
    () => {
      if (this.renderType !== 'original') {
        this.doTranslate()
      }
    },
    1000,
    { trailing: true }
  )

  private updateUserInfoDebounceCall = throttle(async () => {
    await userService.updateUserInfo()
  }, 2000)

  private mouseHoverTranslateThrottleCall = throttle(
    (initContainers: HTMLElementArray) => {
      this.doTranslate('hover', initContainers, this.renderType)
    },
    50,
    { leading: true }
  )

  private markSpecialElThrottleCall = throttle(
    () => {
      const config = this.getConfig()
      const containers = this.getContainers(config)
      this.markContainerSpecialAttr(config, containers)
    },
    500,
    { leading: true }
  )

  private keydownMap: { [key: string]: 1 } = {}
  private lastMousemoveEl: HTMLElement | null = null

  private shadowRootList: ShadowRoot[] = []

  private errorParagraphs: Array<Paragraph> = []

  public styleType: TranslateServiceType.StyleType = 'dualGrey'
  @observable model: ServerDataTypes.Model = MODEL_STRATEGY_CONFIG['pdf'].freeModel
  public glossaryMode: TranslateServiceType.GlossaryMode = 'off'

  public contextType: PostDataTypes.ContextType | undefined = undefined
  public markdownContext: string = ''

  constructor(defaultModel: ServerDataTypes.Model) {
    makeObservable(this)

    this.model = defaultModel

    // this.checkIsBlockPage()
    // this.initMouseHoverTranslate()
  }

  public setModel(model: ServerDataTypes.Model) {
    this.model = model
  }

  public changeStyleType(styleType: TranslateServiceType.StyleType) {
    this.styleType = styleType
    if (styleType === 'onlyTranslation') {
      this.applyTranslationMode()
    } else {
      this.applyTranslationStyleV2(styleType)
    }
  }

  public changeGlossaryMode(glossaryMode: TranslateServiceType.GlossaryMode) {
    this.glossaryMode = glossaryMode || 'off'
  }

  public async toggleRenderType(type: RenderType, doTranslateForTranslation = false) {
    this.renderType = type
    switch (type) {
      case 'original':
        this.doReset()
        break
      case 'parallel':
        this.doReset()
        await this.doTranslate()
        break
      case 'translation':
        await this.doTranslate()
        this.applyTranslationMode()

        break
      default:
        break
    }
  }

  public updateTaskUid() {
    this.taskUid = commonUtils.genId()
  }

  public async doTranslate(
    triggerType: PostDataTypes.UseType = 'pdf',
    initContainers?: HTMLElementArray,
    renderType: RenderType = 'parallel'
  ) {
    this.renderType = renderType
    let selectedRange: Range | null = null
    let endOffset = 0
    let startOffset = 0
    let startContainer: Node | null = null
    let endContainer: Node | null = null
    if (triggerType === 'hover') {
      try {
        const selection = document.getSelection()
        // 检查是否有选中的文本
        if (selection && !selection.isCollapsed) {
          // 获取选中的第一个Range对象
          selectedRange = selection.getRangeAt(0)
          endOffset = selectedRange.endOffset
          startOffset = selectedRange.startOffset
          startContainer = selectedRange.startContainer
          endContainer = selectedRange.endContainer
        }
      } catch (error) {
        //
      }
    }
    const config = this.getConfig()
    const containers = initContainers || this.getContainers(config)

    this.markContainerSpecialAttr(config, containers)
    this.injectCSS()
    this.injectGlobalEffectStyle(config)
    const { inViewportParagraphs, outViewportEls } = await this.getParagraphs(
      containers,
      config,
      () => {
        // NOTE: 如果是悬停翻译，翻译内容插入只有重新设置选中区域
        if (triggerType === 'hover' && selectedRange) {
          try {
            const selection = document.getSelection()
            const range = document.createRange()
            range.setEnd(endContainer!, endOffset)
            range.setStart(startContainer!, startOffset)
            selection!.removeAllRanges()
            selection!.addRange(range)
          } catch (error) {
            //
          }
        }
      }
    )

    // console.log('@@@inViewportParagraphs:', inViewportParagraphs)
    // console.log('@@@outViewportEls:', outViewportEls)

    if (triggerType === 'normal' && this.renderType === 'original') {
      return
    }
    // console.log(inViewportParagraphs, '@@@@@@@@@@@')
    await this.translateParagraphs({
      triggerType,
      paragraphs: inViewportParagraphs,
      from: this.actualApplySourceLang,
      to: userService.pdfProTargetLang,
      action: 'translate',
    })
    if (!initContainers) {
      this.addMutationObserver()
    }
    if (triggerType !== 'hover' && outViewportEls.length) {
      this.addIntersectionObserver({ triggerType, elList: outViewportEls })
    }
  }

  public async doTranslateAll(
    triggerType: PostDataTypes.UseType = 'pdf',
    initContainers?: HTMLElementArray,
    renderType: RenderType = 'parallel',
    progressCallback?: (progress: {
      completed: number
      failed: number
      total: number
      percentage: number
      currentBatch: number
      totalBatches: number
    }) => void // 新增进度回调参数
  ) {
    this.renderType = renderType

    const config = this.getConfig()
    const containers = initContainers || this.getContainers(config)

    this.markContainerSpecialAttr(config, containers)
    this.injectCSS()
    this.injectGlobalEffectStyle(config)

    // 获取所有段落（包括视口内外的）
    const allParagraphs = await this.getAllParagraphs(containers, config, () => {
      // 空回调函数
    })

    let concurrency = 6

    // 分批翻译所有段落
    if (this.model.type === 'ai') {
      let batchSize = 50
      // 其他AI免费模型限制到20个段落
      if (this.model.version === 'free') {
        batchSize = 30
        concurrency = 4
      }

      // AI模型使用固定段落数分批
      await this.translateParagraphsInBatches({
        triggerType,
        paragraphs: allParagraphs,
        from: this.actualApplySourceLang,
        to: userService.pdfProTargetLang,
        batchSize,
        concurrency,
        progressCallback,
      })
    } else {
      let maxBatchSizeBytes = 100 * 1024
      // 免费的模型都慢一点
      if (this.model.version === 'free') {
        maxBatchSizeBytes = 10 * 1024
        concurrency = 2
      }

      // 非AI模型使用字节大小分批
      await this.translateParagraphsInBatches({
        triggerType,
        paragraphs: allParagraphs,
        from: this.actualApplySourceLang,
        to: userService.pdfProTargetLang,
        maxBatchSizeBytes,
        concurrency,
        progressCallback,
      })
    }
  }

  // 修改方法：并发翻译段落
  private async translateParagraphsInBatches({
    paragraphs,
    triggerType,
    from,
    to,
    batchSize,
    maxBatchSizeBytes = 100 * 1024, // 100KB
    concurrency = 5,
    progressCallback, // 新增进度回调参数
  }: {
    paragraphs: Array<Paragraph>
    triggerType: PostDataTypes.UseType
    batchSize?: number
    maxBatchSizeBytes?: number
    concurrency?: number
    progressCallback?: (progress: {
      completed: number
      failed: number
      total: number
      percentage: number
      currentBatch: number
      totalBatches: number
    }) => void // 新增进度回调参数类型
  } & LangOption) {
    // 根据参数优先级选择分批策略
    const batches: Array<Array<Paragraph>> = []

    if (batchSize) {
      // 使用固定段落数分批（AI模型）
      for (let i = 0; i < paragraphs.length; i += batchSize) {
        batches.push(paragraphs.slice(i, i + batchSize))
      }

      console.log(
        `开始并发翻译，共 ${paragraphs.length} 个段落，分为 ${batches.length} 批，每批 ${batchSize} 个段落，并发数 ${concurrency}`
      )
    } else {
      // 使用字节大小动态分批（非AI模型）
      let currentBatch: Array<Paragraph> = []
      let currentBatchSize = 0

      for (const paragraph of paragraphs) {
        const paragraphSize = this.calculateTextSizeInBytes(paragraph.text)

        // 如果当前批次为空，或者添加这个段落不会超过大小限制
        if (currentBatch.length === 0 || currentBatchSize + paragraphSize <= maxBatchSizeBytes) {
          currentBatch.push(paragraph)
          currentBatchSize += paragraphSize
        } else {
          // 当前批次已满，开始新批次
          batches.push(currentBatch)
          currentBatch = [paragraph]
          currentBatchSize = paragraphSize
        }
      }

      // 添加最后一个批次（如果不为空）
      if (currentBatch.length > 0) {
        batches.push(currentBatch)
      }

      const totalTextSize = paragraphs.reduce(
        (sum, p) => sum + this.calculateTextSizeInBytes(p.text),
        0
      )
      console.log(
        `开始并发翻译，共 ${paragraphs.length} 个段落，总文本大小 ${(totalTextSize / 1024).toFixed(
          2
        )}KB，分为 ${batches.length} 批，目标每批最大 ${(maxBatchSizeBytes / 1024).toFixed(
          0
        )}KB，并发数 ${concurrency}`
      )
    }

    // 初始化进度
    let completed = 0
    let failed = 0
    const total = batches.length

    // 触发初始进度回调
    progressCallback?.({
      completed: 0,
      failed: 0,
      total,
      percentage: 0,
      currentBatch: 0,
      totalBatches: total,
    })

    const processBatch = async (batch: Array<Paragraph>, batchIndex: number): Promise<void> => {
      try {
        if (batchSize) {
          // AI模型的日志
          console.log(
            `开始翻译第 ${batchIndex + 1}/${batches.length} 批，包含 ${batch.length} 个段落`
          )
        } else {
          // 非AI模型的日志
          const batchTextSize = batch.reduce(
            (sum, p) => sum + this.calculateTextSizeInBytes(p.text),
            0
          )
          console.log(
            `开始翻译第 ${batchIndex + 1}/${batches.length} 批，包含 ${batch.length
            } 个段落，文本大小 ${(batchTextSize / 1024).toFixed(2)}KB`
          )
        }

        await this.translateParagraphs({
          triggerType,
          paragraphs: batch,
          from,
          to,
          action: 'download',
        })

        completed++
        console.log(`第 ${batchIndex + 1} 批翻译完成 (${completed}/${batches.length})`)
      } catch (error) {
        failed++
        console.error(`翻译第 ${batchIndex + 1} 批时出错:`, error)
        // 不抛出异常，让其他批次继续执行
      }

      // 触发进度回调
      const percentage = Math.round(((completed + failed) / total) * 100)
      progressCallback?.({
        completed,
        failed,
        total,
        percentage,
        currentBatch: completed + failed,
        totalBatches: total,
      })

      // 添加小延迟，避免请求过于频繁
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    // 使用并发池处理
    const executeInPool = async (): Promise<void> => {
      const executing = new Set<Promise<void>>()

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i]
        const promise = processBatch(batch, i)
          .catch((error) => {
            // 即使有错误也不让它冒泡到 Promise.all
            console.error(`Batch ${i + 1} processing failed:`, error)
          })
          .finally(() => {
            executing.delete(promise)
          })

        executing.add(promise)

        // 如果达到并发限制，等待其中一个完成
        if (executing.size >= concurrency) {
          await Promise.race(executing)
        }
      }

      // 等待所有剩余的任务完成
      await Promise.all(executing)
    }

    // 执行翻译，不会因为单个批次失败而停止
    await executeInPool()

    console.log(`所有批次翻译完成，成功: ${completed}，失败: ${failed}`)
  }

  /**
   * 计算文本的UTF-8字节大小
   * @param text 待计算的文本
   * @returns 字节大小
   */
  private calculateTextSizeInBytes(text: string): number {
    return new TextEncoder().encode(text).length
  }

  public doReset() {
    this.errorParagraphs = []
    // cleanAllELEffect
    this.cleanAllElEffect(this.translatableParagraphs || [])
    this.translatableParagraphs = []
    // clear intersectionObserverList
    // this.removeIntersectionObserver()

    this.intersectionObserverList.forEach((observe) => {
      observe.disconnect()
    })
    this.intersectionObserverList = []
    this.removeInjectCSS()
    this.removeGlobalEffectStyle()
    this.removeMutationObserver()
  }

  private hasExcludeAttrAndParentHasExcludeAttr(el: HTMLElement) {
    let curEl = el
    while (curEl) {
      if (this.isMarkAttr(curEl, Consts.CUSTOM_ATTR['EXCLUDE_EL'])) {
        return true
      }
      curEl = curEl.parentElement as HTMLElement
      if (curEl === document.body) {
        break
      }
    }
    return false
  }

  private async translateElementsBatch(batch: HTMLElement[], config: Config) {
    // 生成 paragraphs

    const { wrapperEls, outViewportEls } = this.createTranslatableWrapperElsWrapperEls(
      batch,
      config
    )
    let paragraphs = this.createParagraphs(wrapperEls)
    paragraphs = await this.processShouldTranslate(paragraphs)

    paragraphs = this.mergeParagraphs(paragraphs)
    paragraphs = this.processIsNeedNewLine(paragraphs)
    // console.log('@@@batch', batch)
    // console.log('@@@paragraphs', paragraphs)

    const success = await this.translateParagraphs({
      triggerType: 'pdf',
      paragraphs,
      from: this.actualApplySourceLang,
      to: userService.pdfProTargetLang,
      action: 'download',
    })
    return success
  }

  private addMutationObserver() {
    if (this.mutationObserver) {
      return
    }
    const observe = new MutationObserver((mutations) => {
      // if (!userService.isLogin) {
      //   return
      // }
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((addedNode) => {
          if (addedNode.nodeName.indexOf(Consts.CUSTOM_PREFIX.toUpperCase()) > -1) {
            return
          }
          if (addedNode.nodeType === Node.ELEMENT_NODE) {
            this.mutationObserverThrottleCall()
          }
        })
      })
    })
    observe.observe(document.body, { subtree: true, childList: true })
    for (const shadowRoot of this.shadowRootList) {
      observe.observe(shadowRoot, { subtree: true, childList: true })
    }
    this.mutationObserver = observe
  }

  private addShadowRootObserve(shadowRoot: ShadowRoot) {
    if (this.mutationObserver) {
      this.mutationObserver.observe(shadowRoot, { subtree: true, childList: true })
    } else {
      this.shadowRootList.push(shadowRoot)
    }
  }

  private removeMutationObserver() {
    this.mutationObserver?.disconnect()
    this.mutationObserver = null
  }

  // 监听当前不在视窗内的 element 进入视窗
  private addIntersectionObserver({
    elList,
    triggerType,
  }: {
    elList: HTMLElement[]
    triggerType: PostDataTypes.UseType
  }) {
    elList.forEach((el) => {
      const observe = new IntersectionObserver((entries, observe) => {
        // if (!userService.isLogin) {
        //   return
        // }
        const isInViewport = entries.some((entry) => {
          if (entry.intersectionRatio > 0) {
            observe.disconnect()
            return true
          }
          return false
        })
        if (isInViewport) {
          this.intersectionObserverElList.push(el)
          this.intersectionObserverThrottleCall(triggerType)
        }
      })
      this.setAttr(el, Consts.INTERSECTION_OBSERVER_EL)
      observe.observe(el)
      this.intersectionObserverList.push(observe)
    })
  }

  // 清理方法也需要相应调整
  private removeIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }
  }

  // 词量不足模式
  private applyNoRemailTranslateCountMask() {
    this.translatableParagraphs
      .filter((t) => {
        return t.noRemainTranslateCount
      })
      .forEach((t) => {
        requestAnimationFrame(() => {
          t.translateEl.innerText = t.text
          t.translateEl.classList.add(Consts.TRANSLATE_EL_BLUR_CLASS)
          t.commonParentEl.classList.remove(Consts.LOADING_EL_CLASS)
          if (t.isNeedNewLine || t.isBreakByBrTag) {
            t.translateElWrapper.appendChild(t.upgradeBtnEl)
            t.translateElWrapper.classList.add(Consts.BLOCK_EL_CLASS)
          } else {
            t.translateElWrapper.appendChild(t.upgradeBtnEl)
            t.translateElWrapper.classList.add(Consts.INLINE_EL_CLASS)
          }
        })
      })
    if (this.renderType === 'translation') {
      this.applyTranslationMode()
    }
  }

  private async translateParagraphs({
    paragraphs,
    triggerType,
    from,
    to,
    action,
  }: {
    action: 'translate' | 'download'
    paragraphs: Array<Paragraph>
    triggerType: PostDataTypes.UseType
  } & LangOption) {
    // console.log('translate paragraphs', paragraphs)
    // 处理嵌套了 exclude el 的情况
    paragraphs.forEach((p) => {
      const { replaceTagHtmlList, commonParentEl, isBreakByBrTag } = p
      // 嵌套 br 的情况下，commonParentEl 会包含 N 个 br 元素，渲染的时候每个 br 元素
      // 的译文原本都会渲染到对应的 br 下面，经过下面的逻辑处理后，所有的译文会被合并成一条译文，
      // 导致渲染异常，因此对于嵌套了 br 了的情况，就不做处理。后面需要兼容这种情况
      if (isBreakByBrTag) {
        return
      }
      let text = ''
      const treeWalk = document.createTreeWalker(
        commonParentEl,
        NodeFilter.SHOW_ELEMENT,
        (node) => {
          const el = node as HTMLElement
          // 待翻译元素里包含的 exclude el
          if (
            (this.isMarkAttr(el, Consts.CUSTOM_ATTR['EXCLUDE_EL']) ||
              el.classList.contains('math-ignore')) &&
            Consts.KEEP_STYLE_TAGS.includes(el.tagName)
          ) {
            const curIdx = replaceTagHtmlList.length
            replaceTagHtmlList.push(el.outerHTML)
            text += `<b${curIdx}></b${curIdx}>`
            return NodeFilter.FILTER_REJECT
          }
          // 待翻译文本节点
          if (el.classList.contains(Consts.TEXT_NODE_WRAPPER_CLASS)) {
            text += el.textContent
            return NodeFilter.FILTER_REJECT
          }
          return NodeFilter.FILTER_ACCEPT
        }
      )
      let nextEl = treeWalk.nextNode() as HTMLElement
      while (nextEl) {
        nextEl = treeWalk.nextNode() as HTMLElement
      }
      p.text = text.trim().replace(/\n/g, '')
      if (/<b\d+><\/b\d+>$/.test(p.text)) {
        // 如果最后一个字符是标签，加个点。不然翻译结果可能会缺一个标签
        p.text = `${p.text}.`
      }
    })
    this.renderParagraphsToElement(paragraphs)
    // if (userService.remainTranslateCount <= 0) {
    //   paragraphs.forEach((p) => {
    //     p.noRemainTranslateCount = true
    //   })
    //   this.translatableParagraphs.push(...paragraphs)
    //   this.applyNoRemailTranslateCountMask()
    //   return
    // }

    // console.log('@@@commonParentElList', commonParentElList)
    const context = this.markdownContext
    let success = true

    await pdfProTranslateService.translate(
      {
        paragraphs: paragraphs.map(({ id, text, detectLang, originalText }) => {
          return { id, text, detectLang, originalText }
        }),
        taskUid: this.taskUid,
        originUrl: '',
        originTitle: '',
        triggerType,
        from,
        to,
        context,
        model: this.model,
        action,
        glossaryMode: this.glossaryMode,
      },
      (results: Array<ServiceType.TranslateResult>): void => {
        // console.log('result:', results)
        const renderingResults = this.mergeResults(results, paragraphs)
        // console.log('renderingResults:', renderingResults)
        const noNeedTranslateParagraphs: Array<Paragraph> = []
        for (const r of renderingResults) {
          if (!r.errMsg) {
            this.removeOutViewportElClass(r.commonParentEl)
          }

          if (to === r.detectLang) {
            noNeedTranslateParagraphs.push(r)
          } else {
            const { replaceTagHtmlList } = r
            let result = r.translateResult
            replaceTagHtmlList.forEach((htmlStr, idx) => {
              result = result.replace(`<b${idx}></b${idx}>`, htmlStr)
            })
            r.translateEl.innerHTML = result
            r.commonParentEl.classList.remove(Consts.LOADING_EL_CLASS)

            if (r.errMsg) {
              success = false
              r.translateElWrapper.appendChild(r.actionBoxEl)
              const tipsEl = r.actionBoxEl.querySelector(`.${Consts.TIPS_EL_CLASS}`) as HTMLElement
              const reloadEl = r.actionBoxEl.querySelector(
                `.${Consts.RELOAD_EL_CLASS}`
              ) as HTMLElement
              tipsEl.onclick = (e) => {
                e.stopPropagation()
                e.preventDefault()
                toastController.warning(r.errMsg)
              }
              reloadEl.onclick = (e) => {
                e.stopPropagation()
                e.preventDefault()
                // const commonParentElList = paragraphs.map((p) => {
                //   return p.commonParentEl
                // })
                // this.cleanParagraphsEffect(paragraphs)
                // this.doTranslate(triggerType, commonParentElList, this.renderType)

                const errorParentElList = this.errorParagraphs.map((p) => {
                  return p.commonParentEl
                })
                const config = this.getConfig()
                this.cleanParagraphsEffect(this.errorParagraphs)
                this.errorParagraphs = []
                this.translateElementsBatch(errorParentElList, config)

                // this.doReset()
                // this.doTranslate(triggerType, this.rootContainers, this.renderType)
              }
            } else {
              // 渲染换行
              if (r.isNeedNewLine || r.isBreakByBrTag) {
                r.translateElWrapper.classList.add(Consts.BLOCK_EL_CLASS)
              } else {
                r.translateElWrapper.classList.add(Consts.INLINE_EL_CLASS)
              }
            }

            // this.translatableParagraphs.push(r)
            const existParagraph = this.translatableParagraphs.find((t) => {
              return t.id === r.id
            })
            if (!existParagraph) {
              this.translatableParagraphs.push(r)
            }
          }
        }
        this.cleanParagraphsEffect(noNeedTranslateParagraphs)
        // this.applyTranslationStyle(configController.translateTextStyle)
        // this.applyTranslationStyle('weak')
        this.applyTranslationStyleV2(this.styleType)
        if (this.renderType === 'translation' || this.styleType === 'onlyTranslation') {
          this.applyTranslationMode()
        }
      }
    )

    if (!success) {
      this.errorParagraphs.push(...paragraphs)
    }

    return success
  }

  private mergeResults(
    results: Array<ServiceType.TranslateResult>,
    paragraphs: Array<Paragraph>
  ): Array<Paragraph> {
    const renderResults: Array<Paragraph> = []
    results.forEach((r) => {
      const p = paragraphs.find((p) => p.id === r.id)
      // p 理论上一定存在
      if (p) {
        renderResults.push({
          ...p,
          ...r,
        })
      }
    })
    return [...renderResults]
  }

  private async getParagraphs(
    containers: HTMLElementArray,
    config: Config,
    updateRangeSelection?: () => void
  ): Promise<{
    inViewportParagraphs: Array<Paragraph>
    outViewportEls: HTMLElementArray
  }> {
    // 先给遍历给不再视窗内的元素打上标记
    await this.markIsOutViewport(containers)
    // 生成 paragraphs
    const { wrapperEls, outViewportEls } = this.createTranslatableWrapperElsWrapperEls(
      containers,
      config
    )
    let paragraphs = this.createParagraphs(wrapperEls)
    paragraphs = await this.processShouldTranslate(paragraphs)
    if (updateRangeSelection) {
      updateRangeSelection()
    }
    paragraphs = this.mergeParagraphs(paragraphs)
    paragraphs = this.processIsNeedNewLine(paragraphs)
    // paragraphs = await this.processDetectedLang(paragraphs)
    return {
      inViewportParagraphs: paragraphs,
      outViewportEls,
    }
  }

  public async markIsOutViewport(containers: HTMLElementArray) {
    const allEls: HTMLElementArray = []
    const newRemainingElementsInfo: Array<any> = []
    const fn = (container: HTMLElement) => {
      const treeWalk = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT)
      let curNode = treeWalk.nextNode()
      while (curNode) {
        allEls.push(curNode as HTMLElement)
        curNode = treeWalk.nextNode()
      }
    }
    containers.forEach((container) => {
      fn(container)
    })
    await Promise.all(
      allEls.map(async (el, index) => {
        el.classList.remove(TranslateHelperConsts.OUT_VIEWPORT_EL_CLASS)
        // 如果已经有会译的标识，就不需要在进行检查是否在视窗内
        if (this.isMarkAttr(el, Consts.CUSTOM_ATTR['EXCLUDE_EL'])) {
          return
        }
        if (this.isMarkAttr(el, Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL'])) {
          return
        }
        if (el.tagName.startsWith(Consts.CUSTOM_PREFIX.toUpperCase())) {
          return
        }
        if (el.id === envHelper.withExtensionPrefix('content-root')) {
          return
        }
        const inViewPort = await this.checkElIsInViewport(el)
        if (!inViewPort) {
          // 这里还应该检查一下，如果只是一个行内元素就应该一起翻译
          if (this.isBlockEl(el)) {
            el.classList.add(TranslateHelperConsts.OUT_VIEWPORT_EL_CLASS)
          }
        } else {
          // 有一种情况是父元素 inViewPort 为false,但是子元素是在视图上的
          if (el.parentElement) {
            this.removeOutViewportElClass(el.parentElement)
          }
        }
      })
    )
  }

  private removeOutViewportElClass(el: HTMLElement) {
    if (el) {
      el.classList.remove(TranslateHelperConsts.OUT_VIEWPORT_EL_CLASS)
      if (el.parentElement) {
        this.removeOutViewportElClass(el.parentElement)
      }
    }
  }

  private checkElIsInViewport(el: HTMLElement): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      new IntersectionObserver((entries, observe) => {
        const isInViewport = entries.some((entry) => {
          if (entry.intersectionRatio > 0) {
            observe.disconnect()
            return true
          }
          return false
        })
        resolve(isInViewport)
      }).observe(el)
    })
  }

  private createTranslatableWrapperElsWrapperEls(
    containers: HTMLElementArray,
    config: Config
  ): { wrapperEls: Array<TranslatableWrapper>; outViewportEls: HTMLElementArray } {
    const wrapperEls: Array<TranslatableWrapper> = []
    const textNodes: Array<Text> = []
    const outViewportEls: HTMLElementArray = []
    // 生成 textNodes
    const fn = (container: HTMLElement) => {
      const treeWalk = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        (node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement
            // 会译也是用 shadow root 的方式插入的，要直接过滤掉，不然也会监听进行翻译
            if (el.id === envHelper.withExtensionPrefix('content-root')) {
              return NodeFilter.FILTER_REJECT
            }
            if (this.isMarkAttr(el, Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL'])) {
              return NodeFilter.FILTER_REJECT
            }
            if (this.isMarkAttr(el, Consts.CUSTOM_ATTR['EXCLUDE_EL'])) {
              return NodeFilter.FILTER_REJECT
            }
            // 解决点开网页翻译后 进行视频切换， 此时页面未刷新，仅仅文本节点发生变化，但是没有进行翻译的问题。比如下面这个地址：
            // https://app.jove.com/v/52531/a-protocol-for-lentiviral-transduction-and-downstream-analysis-of-intestinal-organoids
            // if (this.isMarkAttr(el, Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL'])) {
            //   return NodeFilter.FILTER_REJECT
            // }
            if (el.tagName.startsWith(Consts.CUSTOM_PREFIX.toUpperCase())) {
              return NodeFilter.FILTER_REJECT
            }
            if (el.classList.contains('math-ignore')) {
              return NodeFilter.FILTER_REJECT
            }
            if (el.classList.contains(TranslateHelperConsts.OUT_VIEWPORT_EL_CLASS)) {
              if (!this.isMarkAttr(el, Consts.INTERSECTION_OBSERVER_EL)) {
                outViewportEls.push(el)
              }
              return NodeFilter.FILTER_REJECT
            }
            // if (el.shadowRoot && configController.enableShadowRootTranslate) {
            //   // shadowRoot 需要特殊处理
            //   this.markSpecialAttr(config, el.shadowRoot as any)
            //   this.addShadowRootObserve(el.shadowRoot)
            //   fn(el.shadowRoot as any)
            // }

            return NodeFilter.FILTER_SKIP
          }

          return NodeFilter.FILTER_ACCEPT
        }
      )
      let textNode = treeWalk.nextNode() as Text
      while (textNode) {
        const textContent = textNode.textContent?.trim()
        if (textContent) {
          textNodes.push(textNode)
        }
        textNode = treeWalk.nextNode() as Text
      }
    }
    containers.forEach((container) => {
      fn(container)
    })
    // 生成 wrapperEl 和 textNodeWrapperEls
    textNodes.forEach((textNode) => {
      const { textNodeWrapperEl, wrapperEl } = this.createTranslatableNodeWrapper(textNode)
      wrapperEls.push({
        wrapperEl,
        textNodeWrapperEl,
      })
    })
    return { wrapperEls, outViewportEls }
  }

  private createParagraphs(wrapperEls: Array<TranslatableWrapper>): Array<Paragraph> {
    const paragraphs: Array<Paragraph> = []
    for (const { wrapperEl, textNodeWrapperEl } of wrapperEls) {
      const parentEl = this.findTextNodeParentEl(wrapperEl, this.getBlockParentEl(wrapperEl))
      const paragraph = paragraphs.find((p) => {
        return p.commonParentEl === parentEl
      })
      if (paragraph) {
        paragraph.translatableWrapperEls.push({ textNodeWrapperEl, wrapperEl })
        paragraph.text += textNodeWrapperEl.textContent as string
        // console.log('text1', paragraph.text)
      } else {
        const text = textNodeWrapperEl.textContent as string
        const { translateEl, translateElWrapper, actionBoxEl, upgradeBtnEl } =
          this.createTranslateEl()
        this.setAttr(parentEl, Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL'])
        this.setAttr(parentEl, Consts.CUSTOM_ATTR['TARGET_LANG'], userService.pdfTargetLang)
        // console.log('text2', text)
        paragraphs.push({
          id: commonUtils.genId(),
          commonParentEl: parentEl,
          translatableWrapperEls: [{ textNodeWrapperEl, wrapperEl }],
          text,
          originalText: text,
          translateEl,
          translateElWrapper,
          isNeedNewLine: false,
          isBreakByBrTag: false,
          actionBoxEl,
          upgradeBtnEl,
          detectLang: '',
          replaceTagHtmlList: [],
          errMsg: null,
          translateResult: '',
        })
      }
    }
    return [...paragraphs]
  }

  private async processShouldTranslate(paragraphs: Array<Paragraph>): Promise<Array<Paragraph>> {
    const translateParagraphs: Array<Paragraph> = []
    await Promise.all(
      paragraphs.map(async (paragraph) => {
        // 去除前后空格，换行
        paragraph.text = paragraph.text.trim()
        // 去除文本内换行
        paragraph.text = paragraph.text.replace(/\n/g, '')
        if (await this.shouldTranslate(paragraph)) {
          translateParagraphs.push(paragraph)
        }
      })
    )
    this.cleanParagraphsEffect(
      paragraphs.filter((p) => {
        return !translateParagraphs.includes(p)
      })
    )
    // const noNeedTranslateParagraphs = paragraphs.filter((p) => {
    //   return !translateParagraphs.includes(p)
    // })
    // this.cleanParagraphsEffect(noNeedTranslateParagraphs)
    // noNeedTranslateParagraphs.forEach((p) => {
    //   this.markNoTranslateEl(p.commonParentEl)
    // })
    return [...translateParagraphs]
  }

  private cleanParagraphsEffect(paragraphs: Array<Paragraph>) {
    // console.log('clean paragraphs effect', paragraphs)
    paragraphs.forEach(
      ({ commonParentEl, translatableWrapperEls, translateElWrapper: translateWrapperEl }) => {
        this.removeAllCustomAttr(commonParentEl)
        translateWrapperEl.remove()
        translatableWrapperEls.forEach(({ textNodeWrapperEl, wrapperEl }) => {
          const textNode = textNodeWrapperEl.firstChild as Text
          wrapperEl.after(textNode)
          wrapperEl.remove()
        })
      }
    )
  }

  private cleanNoRemainEffect() {
    const noRemainParagraphs = this.translatableParagraphs.filter((t) => {
      return t.noRemainTranslateCount
    })
    this.cleanAllElEffect(noRemainParagraphs)
    this.translatableParagraphs = this.translatableParagraphs.filter((t) => {
      return !t.noRemainTranslateCount
    })
  }

  private cleanAllElEffect(paragraphs: Array<Paragraph>) {
    // console.log('clean all effect', paragraphs)
    // clear commonParentEl attr
    const commonParentElSelector = `[${Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL']}=${Consts.CUSTOM_ATTR_VALUE}]`
    document.body.querySelectorAll(commonParentElSelector).forEach((el) => {
      this.removeAllCustomAttr(el as HTMLElement)
    })
    // clear intersectionEl attr
    const intersectionElSelector = `[${Consts.INTERSECTION_OBSERVER_EL}=${Consts.CUSTOM_ATTR_VALUE}]`
    document.body.querySelectorAll(intersectionElSelector).forEach((el) => {
      this.removeAllCustomAttr(el as HTMLElement)
    })
    // clear exclude el attr
    const excludeElSelector = `[${Consts.CUSTOM_ATTR['EXCLUDE_EL']}=${Consts.CUSTOM_ATTR_VALUE}]`
    document.body.querySelectorAll(excludeElSelector).forEach((el) => {
      this.removeAllCustomAttr(el as HTMLElement)
    })
    // clear block el effect
    const blockElSelector = `[${Consts.CUSTOM_ATTR['BLOCK_EL']}=${Consts.CUSTOM_ATTR_VALUE}]`
    document.body.querySelectorAll(blockElSelector).forEach((el) => {
      this.removeAllCustomAttr(el as HTMLElement)
    })
    // clear inline el effect
    const inlineElSelector = `[${Consts.CUSTOM_ATTR['INLINE_EL']}=${Consts.CUSTOM_ATTR_VALUE}]`
    document.body.querySelectorAll(inlineElSelector).forEach((el) => {
      this.removeAllCustomAttr(el as HTMLElement)
    })
    // clear textNode effect
    const wrapperElSelector = Consts.TRANSLATABLE_NODE_WRAPPER_CLASS
    document.body.querySelectorAll(`.${wrapperElSelector}`).forEach((el) => {
      const textNode = el.firstChild?.firstChild as Text
      el.after(textNode)
      el.remove()
    })
    // remove translate el
    paragraphs.forEach(({ translateElWrapper, commonParentEl }) => {
      translateElWrapper.remove()
    })
  }

  private async shouldTranslate(paragraph: Paragraph): Promise<boolean> {
    const isValid = this.textIsValid(paragraph.text)
    // console.log('@@@textIsValid:', paragraph.text, isValid)
    if (!isValid) {
      return false
    }
    // const textLang = await this.detectTextLang(paragraph.text)
    // if (textLang) {
    //   paragraph.detectLang = textLang
    // }
    // if (textLang && textLang === configController.targetLang) {
    //   return false
    // }
    return true
  }

  private textIsValid(text: string): boolean {
    const trimText = text.trim()
    if (!trimText) {
      return false
    }

    // 检查是否为URL
    const urlRegex = /^(?:https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i
    if (urlRegex.test(trimText)) {
      return false
    }

    // 检查是否为纯数字（包括带空格、逗号、点号的各种数字格式）
    // 匹配：123, 1,234, 1.234, 84 764,989, 1 234.56, 123.45, 26.2 .5等
    const numberWithSeparatorsRegex = /^[-+]?[\d\s,.'-]+$/
    if (numberWithSeparatorsRegex.test(trimText)) {
      // 移除所有分隔符后检查是否只剩数字和小数点
      const cleanedText = trimText.replace(/[\s,'\-]/g, '')
      // 检查清理后是否为有效数字格式
      if (
        /^[-+]?\d*\.?\d+$/.test(cleanedText) ||
        /^\d+$/.test(cleanedText) ||
        /^\d*\.\d+$/.test(cleanedText) ||
        /^\d+\.\d*$/.test(cleanedText)
      ) {
        return false
      }
    }

    // 额外检查：主要由数字、空格、点号、逗号、连字符组成的文本
    // 如果去掉这些字符后剩余字符很少，则认为是数字相关内容
    const nonNumberChars = trimText.replace(/[\d\s,.'-]/g, '')
    const numberChars = trimText.replace(/[^\d\s,.'-]/g, '')
    if (numberChars.length > 0 && nonNumberChars.length <= 1) {
      return false
    }

    // 专门检查数字范围格式：2-22, 1-10, 100-200等
    if (/^\d+\s*-\s*\d+$/.test(trimText)) {
      return false
    }

    // 检查版本号或章节号格式：5.1.1, 1.2.3, 10.5.2等
    if (/^\d+(\.\d+)+$/.test(trimText)) {
      return false
    }

    // 检查带货币符号和单位的数字
    // 匹配：$1.3, ¥10, €100, 1k, 1.2M, 1h, 1.5min等
    const currencyUnitRegex = /^[$￥€£¢¥]?[-+]?[\d\s,.']+[kKmMbBhHminMINsecSEC%]?$/
    if (currencyUnitRegex.test(trimText)) {
      const cleanedText = trimText.replace(/[$￥€£¢¥kKmMbBhHminMINsecSEC%\s,'-]/g, '')
      if (/^[-+]?\d*\.?\d+$/.test(cleanedText)) {
        return false
      }
    }

    // 检查分数格式：1/2, 3/4, 12/34等
    if (/^\d+\/\d+$/.test(trimText)) {
      return false
    }

    // 检查是否为纯符号或特殊字符
    // 匹配：--, ---, ..., ***, ===, +++, <<<, >>>, ###等
    // 使用更全面的字符检测，排除所有字母和数字字符
    if (/^[^\p{L}\p{N}]+$/u.test(trimText)) {
      return false
    }

    // 检查特殊标签（以特定符号开头的短字符串）
    const specialTags = ['#', '@', '$']
    if (specialTags.includes(trimText[0]) && trimText.length <= 10) {
      return false
    }

    // 检查最小文本长度
    const minTextCount = 2
    if (trimText.length < minTextCount) {
      return false
    }

    return true
  }

  private mergeParagraphs(paragraphs: Array<Paragraph>): Array<Paragraph> {
    const firstMergedParagraphs: Array<Paragraph> = []
    // 处理 commonParentEl 嵌套 commonParentEl 的情况
    paragraphs.forEach((paragraph) => {
      const selector = `[${Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL']}=${Consts.CUSTOM_ATTR_VALUE}]`
      if (paragraph.commonParentEl.querySelector(selector)) {
        paragraph.translatableWrapperEls.forEach(({ wrapperEl, textNodeWrapperEl }) => {
          const { translateEl, translateElWrapper, actionBoxEl, upgradeBtnEl } =
            this.createTranslateEl()
          const commonParentEl = wrapperEl
          this.setAttr(commonParentEl, Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL'])
          this.setAttr(commonParentEl, Consts.CUSTOM_ATTR['TARGET_LANG'], userService.pdfTargetLang)
          firstMergedParagraphs.push({
            ...paragraph,
            id: commonUtils.genId(),
            text: wrapperEl.innerText as string,
            translatableWrapperEls: [{ textNodeWrapperEl, wrapperEl }],
            translateEl,
            translateElWrapper,
            commonParentEl,
            actionBoxEl,
            upgradeBtnEl,
          })
        })
        this.removeAllCustomAttr(paragraph.commonParentEl)
      } else {
        firstMergedParagraphs.push(paragraph)
      }
    })
    const mergedParagraphs: Array<Paragraph> = []
    // 处理嵌套 br 的情况
    const addParagraph = (wrapperEls: Array<TranslatableWrapper>, paragraph: Paragraph) => {
      if (wrapperEls.length === 0) {
        return
      }
      const { translateEl, translateElWrapper, actionBoxEl, upgradeBtnEl } =
        this.createTranslateEl()
      const fakeCommonParentEl = document.createElement('div')
      wrapperEls.forEach(({ wrapperEl }) => {
        fakeCommonParentEl.appendChild(wrapperEl.cloneNode(true))
      })
      mergedParagraphs.push({
        ...paragraph,
        id: commonUtils.genId(),
        text: fakeCommonParentEl.innerText.trim().replace(/\n/g, ''),
        translatableWrapperEls: wrapperEls,
        translateEl,
        translateElWrapper,
        isBreakByBrTag: true,
        actionBoxEl,
        upgradeBtnEl,
      })
    }
    firstMergedParagraphs.forEach((paragraph) => {
      const brElList = Array.from(paragraph.commonParentEl.querySelectorAll('br')).filter((el) => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return (el as any).checkVisibility()
        } catch (error) {
          return true
        }
      })
      if (brElList.length > 0) {
        let curBrIdx = 0
        let { translatableWrapperEls } = paragraph
        while (curBrIdx < brElList.length) {
          const curBrEl = brElList[curBrIdx]
          const newTranslatableWrapperEls: Array<TranslatableWrapper> = []
          translatableWrapperEls.forEach(({ wrapperEl, textNodeWrapperEl }) => {
            if (wrapperEl.compareDocumentPosition(curBrEl) & Node.DOCUMENT_POSITION_FOLLOWING) {
              newTranslatableWrapperEls.push({ wrapperEl, textNodeWrapperEl })
            }
          })
          translatableWrapperEls = translatableWrapperEls.filter((t) => {
            return !newTranslatableWrapperEls.find((n) => {
              return n.wrapperEl === t.wrapperEl
            })
          })
          addParagraph(newTranslatableWrapperEls, paragraph)
          curBrIdx += 1
        }
        addParagraph(translatableWrapperEls, paragraph)
      } else {
        mergedParagraphs.push(paragraph)
      }
    })
    return [...mergedParagraphs]
  }

  private renderParagraphsToElement(paragraphs: Array<Paragraph>) {
    paragraphs.forEach((p) => {
      const {
        isNeedNewLine,
        commonParentEl,
        translateElWrapper,
        translateEl,
        translatableWrapperEls,
        isBreakByBrTag,
      } = p
      if (isBreakByBrTag) {
        const wrapperEl = translatableWrapperEls[translatableWrapperEls.length - 1].wrapperEl
        wrapperEl.appendChild(translateElWrapper)
        return
      }
      if (!isNeedNewLine) {
        translateEl.before(this.createSpaceEl())
        translateEl.after(this.createSpaceEl())
      }
      if (translatableWrapperEls.length > 1) {
        commonParentEl.appendChild(translateElWrapper)
      } else {
        translatableWrapperEls[0].wrapperEl.appendChild(translateElWrapper)
      }
      commonParentEl.classList.add(Consts.LOADING_EL_CLASS)
    })
  }

  private processIsNeedNewLine(paragraphs: Array<Paragraph>): Array<Paragraph> {
    paragraphs.forEach((p) => {
      if (p.isBreakByBrTag) {
        return
      }
      let isNeedNewLine = false
      if (this.isBlockEl(p.commonParentEl)) {
        const blockParentEl = this.getBlockParentEl(p.commonParentEl)
        isNeedNewLine = true
        const { display, flexDirection } = window.getComputedStyle(blockParentEl)
        if (display === 'flex' && flexDirection === 'row') {
          isNeedNewLine = false
        }
      }
      const text = p.text
      const blockMinTextCount = 32
      const blockMinWordCount = 5
      const wordCount = text.split(' ').length
      const textCount = text.length
      const lineCount = text.split('\n').length
      // 长度都未超出时，强制不换行
      if (wordCount < blockMinWordCount && textCount < blockMinTextCount && lineCount < 2) {
        isNeedNewLine = false
      }
      // 如果是手动标记了块级元素，则默认换行；解决块级元素字数较少，不换行样式会变形的问题
      if (this.isMarkAttr(p.commonParentEl, Consts.CUSTOM_ATTR['BLOCK_EL'])) {
        isNeedNewLine = true
      }
      // 长度超出后强制换行
      if (wordCount > blockMinWordCount || textCount > blockMinTextCount || lineCount >= 2) {
        isNeedNewLine = true
      }
      p.isNeedNewLine = isNeedNewLine
    })
    return [...paragraphs]
  }

  // private async processDetectedLang(paragraphs: Array<Paragraph>): Promise<Array<Paragraph>> {
  //   const allParagraphs = await Promise.all(
  //     paragraphs.map(async (p) => {
  //       const detectLang = await translateHelper.detectTextLang(p.text)
  //       p.detectLang = detectLang
  //       return p
  //     })
  //   )
  //   return allParagraphs
  // }

  // 确定文本节点的父元素
  private findTextNodeParentEl(el: HTMLElement, blockParentEl: HTMLElement): HTMLElement {
    const parentEl = el.parentElement as HTMLElement
    if (!parentEl) {
      return el
    }
    if (parentEl.tagName === 'BODY') {
      return parentEl
    }
    if (blockParentEl === parentEl) {
      return blockParentEl
    }
    if (blockParentEl.textContent?.trim() === parentEl.textContent?.trim()) {
      return blockParentEl
    }
    return this.findTextNodeParentEl(parentEl, blockParentEl)
  }

  private getBlockParentEl(el: HTMLElement): HTMLElement {
    if (el.tagName === 'BODY') {
      return el
    }
    const parentEl = el.parentElement as HTMLElement
    if (!parentEl) {
      // shadow root 的根节点，父元素是null
      return el
    }
    if (this.isBlockEl(parentEl)) {
      return parentEl
    }
    if (parentEl.tagName === 'BODY') {
      return parentEl
    }
    return this.getBlockParentEl(parentEl)
  }

  private isInlineEl(el: HTMLElement): boolean {
    if (!(el instanceof HTMLElement) || !el) {
      return false
    }
    if (this.isMarkAttr(el, Consts.CUSTOM_ATTR['INLINE_EL'])) {
      return true
    }
    if (this.isMarkAttr(el, Consts.CUSTOM_ATTR['BLOCK_EL'])) {
      return false
    }
    const { display } = window.getComputedStyle(el)
    const inlineDisplayValues = ['inline', 'inline-block', 'inline-flex', 'inline-grid']
    if (inlineDisplayValues.includes(display)) {
      return true
    }
    return false
  }

  private isBlockEl(el: HTMLElement): boolean {
    return !this.isInlineEl(el)
  }

  private createSpaceEl(): HTMLElement {
    const el = document.createElement(Consts.CUSTOM_TAG)
    el.classList.add(Consts.SPACE_EL_CLASS)
    el.innerHTML = '&nbsp;'
    return el
  }

  private createTranslateEl() {
    const translateElWrapper = document.createElement(Consts.CUSTOM_TAG)
    translateElWrapper.classList.add(Consts.TRANSLATE_EL_WRAPPER_CLASS)
    const translateEl = document.createElement(Consts.CUSTOM_TAG)
    translateEl.classList.add(Consts.TRANSLATE_EL_CLASS)
    translateElWrapper.appendChild(translateEl)

    // 升级操作框
    const upgradeBtnEl = document.createElement(Consts.CUSTOM_TAG)
    upgradeBtnEl.innerText = '解锁会员查看译文'
    upgradeBtnEl.classList.add(Consts.UPGRADE_BTN_EL_CLASS)
    upgradeBtnEl.onclick = (e) => {
      e.stopPropagation()
      e.preventDefault()
    }

    // 错误提示操作框
    const actionBoxEl = document.createElement(Consts.CUSTOM_TAG)
    actionBoxEl.classList.add(Consts.ACTION_BOX_EL_CLASS)

    const tipsEl = document.createElement('img')
    tipsEl.src = 'https://assets.weibanzhushou.com/web/pt-web/icon__warn.f894a1ba650f.png'
    tipsEl.classList.add(Consts.TIPS_EL_CLASS)

    const reloadEl = document.createElement('img')
    reloadEl.src = 'https://assets.weibanzhushou.com/web/pt-web/icon__reload.6ac9d27818ba.png'
    reloadEl.classList.add(Consts.RELOAD_EL_CLASS)

    actionBoxEl.appendChild(tipsEl)
    actionBoxEl.appendChild(reloadEl)

    return { translateElWrapper, translateEl, actionBoxEl, upgradeBtnEl }
  }

  private createTranslatableNodeWrapper(textNode: Text): TranslatableWrapper {
    const wrapperEl = document.createElement(Consts.CUSTOM_TAG)
    wrapperEl.classList.add(Consts.TRANSLATABLE_NODE_WRAPPER_CLASS)
    const textNodeWrapperEl = document.createElement(Consts.CUSTOM_TAG)
    textNodeWrapperEl.classList.add(Consts.TEXT_NODE_WRAPPER_CLASS)
    textNode.after(wrapperEl)
    textNodeWrapperEl.appendChild(textNode)
    wrapperEl.appendChild(textNodeWrapperEl)
    return { wrapperEl, textNodeWrapperEl }
  }

  private getContainers(config: Config): HTMLElementArray {
    const { area, containerSelectors } = config
    let containers: HTMLElementArray = []
    const rootEl = document.documentElement
    if (area === 'all') {
      containers = [rootEl]
    } else {
      const elements = this.getElementsBySelectors(rootEl, containerSelectors)
      containers = containers.concat(...elements)
      if (containers.length === 0) {
        containers = [rootEl]
      }
    }
    return containers
  }

  private markContainerSpecialAttr(config: Config, containers: HTMLElementArray) {
    containers.forEach((container) => {
      this.markSpecialAttr(config, container)
    })
  }

  private markSpecialAttr(config: Config, rootEl: HTMLElement) {
    const { excludeSelectors, blockSelectors, inlineSelectors, includeSelectors } = config
    excludeSelectors.forEach((selector) => {
      const elList = rootEl.querySelectorAll(selector) as NodeListOf<HTMLElement>
      elList.forEach((el) => {
        this.setAttr(el, Consts.CUSTOM_ATTR['EXCLUDE_EL'])
      })
    })
    includeSelectors.forEach((selector) => {
      const elList = rootEl.querySelectorAll(selector) as NodeListOf<HTMLElement>
      elList.forEach((el) => {
        this.removeAttr(el, Consts.CUSTOM_ATTR['EXCLUDE_EL'])
      })
    })
    blockSelectors.forEach((selector) => {
      const elList = rootEl.querySelectorAll(selector) as NodeListOf<HTMLElement>
      elList.forEach((el) => {
        this.setAttr(el, Consts.CUSTOM_ATTR['BLOCK_EL'])
      })
    })
    inlineSelectors.forEach((selector) => {
      const elList = rootEl.querySelectorAll(selector) as NodeListOf<HTMLElement>
      elList.forEach((el) => {
        this.setAttr(el, Consts.CUSTOM_ATTR['INLINE_EL'])
      })
    })
  }

  private getElementsBySelectors(rootEl: HTMLElement, selectors: string[]): HTMLElementArray {
    const els: HTMLElementArray = []
    selectors.forEach((selector) => {
      const el = rootEl.querySelector(selector)
      if (el) {
        els.push(el as HTMLElement)
      }
    })
    return els
  }

  private getConfig(): Config {
    const config = commonUtils.jsonClone(TRANSLATE_HELPER_CONFIG)
    const siteRule = userService.siteRules.find(({ matches, excludeMatches }) => {
      const url = document.location.href
      const urlIsNoMatched = commonUtils.checkUrlIsMatched(url, excludeMatches!)
      if (urlIsNoMatched) {
        return urlIsNoMatched
      }
      const urlIsMatched = commonUtils.checkUrlIsMatched(url, matches)
      return urlIsMatched
    })
    if (siteRule) {
      const {
        excludeSelectors = [],
        containerSelectors = [],
        globalStyles = {},
        blockSelectors = [],
        inlineSelectors = [],
        includeSelectors = [],
        globalStylesV1 = {},
        reserveSelectors = [],
      } = siteRule
      config.globalStyles = {
        ...config.globalStyles,
        ...globalStyles,
      }
      config.globalStylesV1 = {
        ...config.globalStylesV1,
        ...globalStylesV1,
      }
      config.containerSelectors.push(...containerSelectors)
      config.excludeSelectors.push(...excludeSelectors)
      config.includeSelectors.push(...includeSelectors)
      config.blockSelectors.push(...blockSelectors)
      config.inlineSelectors.push(...inlineSelectors)
      config.excludeSelectors = config.excludeSelectors.filter((s) => {
        return !reserveSelectors.includes(s)
      })
    }
    return config
  }

  private isMarkAttr(el: HTMLElement, attr: string, value = Consts.CUSTOM_ATTR_VALUE) {
    if (!el) {
      return false
    }
    return el.getAttribute(attr) === value
  }

  private setAttr(el: HTMLElement, attr: string, value = Consts.CUSTOM_ATTR_VALUE) {
    el.setAttribute(attr, value)
  }

  private removeAttr(el: HTMLElement, attr: string) {
    el.removeAttribute(attr)
  }

  private removeAllCustomAttr(el: HTMLElement) {
    Array.from(el.attributes).forEach(({ name }) => {
      if (name.startsWith(Consts.CUSTOM_PREFIX)) {
        this.removeAttr(el, name)
      }
    })
  }

  private injectGlobalEffectStyle(config: Config) {
    if (document.querySelector(`.${Consts.GLOBAL_EFFECT_STYLE_CLASS}`)) {
      return
    }
    const styleEl = document.createElement('style')
    styleEl.classList.add(Consts.GLOBAL_EFFECT_STYLE_CLASS)
    const { globalStyles, globalStylesV1 } = config
    const styleStr = Object.keys(globalStyles).reduce((pre, cur) => {
      return `${pre}
              ${cur} {
                ${globalStyles[cur]}
              }`
    }, '')
    const globalStyleStrV1 = `
      [${Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL']}] {
        ${styleStr}
      }`
    const globalStyleStrV2 = Object.keys(globalStyles).reduce((pre, cur) => {
      return `${pre}
              ${cur}[${Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL']}] {
                ${globalStyles[cur]}
              }`
    }, '')

    const globalStyleStrV3 = Object.keys(globalStylesV1).reduce((pre, cur) => {
      return `${pre}
              ${cur} {
                ${globalStylesV1[cur]}
              }`
    }, '')
    const globalStyleStr = `${globalStyleStrV1} ${globalStyleStrV2} ${globalStyleStrV3}`
    styleEl.textContent = globalStyleStr
    document.head.appendChild(styleEl)
  }

  private removeGlobalEffectStyle() {
    const globalStyleEl = document.querySelector(`.${Consts.GLOBAL_EFFECT_STYLE_CLASS}`)
    globalStyleEl?.remove()
  }

  private injectCSS() {
    if (document.querySelector(`[${Consts.CUSTOM_ATTR['INJECT_STYLE_EL']}]`)) {
      return
    }
    const styleEl = document.createElement('style')
    this.setAttr(styleEl, Consts.CUSTOM_ATTR['INJECT_STYLE_EL'])
    styleEl.textContent = `
      .${Consts.LOADING_EL_CLASS}:after {
        content: ' ';
        width: 12px !important;
        height: 12px !important;
        vertical-align: middle;
        display: inline-block !important;
        margin: 0 4px;
        border: 2px solid #B0B0B0 !important;
        border-top-color: #EFECF2 !important;
        border-right-color: #EFECF2 !important;
        border-bottom-color: #EFECF2 !important;
        border-radius: 100%;
        background:transparent !important;
        flex-grow:unset;
        animation: huiyi-translate-loading-circle-animation infinite 0.75s linear;
      }
      @keyframes huiyi-translate-loading-circle-animation {
        0% {
          transform: rotate(0);
        }
        100% {
          transform: rotate(360deg);
        }
      }
      .${Consts.WAVY_THEME_CLASS} {
        text-decoration: underline wavy #febc2e 1.5px;
        -webkit-text-decoration: underline wavy #febc2e 1.5px;
        text-underline-offset: 4px;
      }
      .${Consts.WEAK_THEME_CLASS} {
        opacity: 1;
      }
      .${Consts.HIDE_EL_CLASS} {
        display: none !important;
      }
      .${Consts.ACTION_BOX_EL_CLASS} {
        display: inline-flex;
        justify-content: flex-start;
        align-items: center;
        height: 1em;
        vertical-align: middle;
      }
      .${Consts.ACTION_BOX_EL_CLASS} img:hover {
        opacity: 0.8;
      }
      .${Consts.ACTION_BOX_EL_CLASS} img {
        height: 100%;
        max-height:100%;
        cursor: pointer;
        user-select: none;
        -webkit-user-drag: none;
        -webkit-tap-highlight-color: transparent;
        object-fit: contain;
        margin-left: 4px;
      }
      .${Consts.TRANSLATE_EL_WRAPPER_CLASS} {
        position: relative;
      }
      .${Consts.TRANSLATE_EL_WRAPPER_CLASS}:hover .${Consts.UPGRADE_BTN_EL_CLASS} {
        opacity: 1;
      }
      .${Consts.TRANSLATE_EL_BLUR_CLASS} {
        -webkit-filter: blur(10px);
        -moz-filter: blur(10px);
        -ms-filter: blur(10px);
        filter: blur(10px);
      }
      .${Consts.UPGRADE_BTN_EL_CLASS} {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: 10;
        cursor: pointer;
        user-select: none;
        -webkit-user-drag: none;
        -webkit-tap-highlight-color: transparent;
        background: #FFFFFF;
        box-shadow: 0px 2px 8px 0px rgba(0,0,0,0.08);
        border-radius: 8px;
        font-size: 13px;
        font-family: PingFangSC-Regular, PingFang SC;
        font-weight: 400;
        color: ${themeHelper.themeToken.colorPrimary};
        line-height: 32px;
        height: 32px;
        padding: 0 16px;
        text-indent:0;
        opacity: 0;
        word-break:keep-all;
      }
      .${Consts.UPGRADE_BTN_EL_CLASS}:hover {
        box-shadow: 0px 3px 10px 0px rgba(0,0,0,0.1);
      }
      .${Consts.BLOCK_EL_CLASS} {
        display: inline-block;
      }

      .${Consts.BACKGROUND_GREY_EL_CLASS} {
        background-color: #e3e5e8de;
        padding: 6px 8px;
        border-radius: 4px;
      }

      .${Consts.INLINE_EL_CLASS}{
        padding: 4px;
        display: inline-block;
      }

      .${Consts.TEXT_NODE_WRAPPER_CLASS},.${Consts.TRANSLATE_EL_CLASS},.${Consts.TRANSLATABLE_NODE_WRAPPER_CLASS},.${Consts.TRANSLATE_EL_WRAPPER_CLASS}{
        color:inherit;
        visibility:inherit !important;
        font-family: 'CMU Serif', 'Georgia', Helvetica, Arial, sans-serif;
      }

      /* 原文大小调整 - 使用 CSS 变量影响布局空间 */
      .${Consts.TEXT_NODE_WRAPPER_CLASS} {
        font-size: calc(1em * var(--original-text-scale, 1)) !important;
      }

      /* 译文大小调整 - 使用 CSS 变量影响布局空间 */
      .${Consts.TRANSLATE_EL_CLASS} {
        font-size: calc(1em * var(--translation-text-scale, 1)) !important;
      }

      .${Consts.COLLECT_BOX} {
        visibility: hidden !important;
        display:inline-block !important;
        position: relative !important;
        box-sizing: border-box !important;
        vertical-align: -0.125em !important;
        width: 20px !important;
        height: 20px !important;
        background: #F3EBFF;
        box-shadow: 0px 1px 6px 1px rgba(51,25,81,0.11);
        border-radius: 12px;
        border: 1px solid #FFFFFF;
        cursor: pointer;
        user-select: none;
        -webkit-user-drag: none;
        -webkit-tap-highlight-color: transparent;
      }

      .${Consts.COLLECT_BOX}.active {
        visibility: visible !important;
      }

      #${Consts.CUSTOM_PREFIX} .${Consts.COLLECT_ICON} {
        width: 14px !important;
        height: 14px !important;
        position: absolute !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;
        margin:0 !important;
        padding:0 !important;
        background:transparent !important;
      }

       .${Consts.COMPARE_BOX} {
        display:inline-flex !important;
        position: relative !important;
        box-sizing: border-box !important;
        vertical-align: -0.125em !important;
        width: 14px !important;
        height: 14px !important;
        background: #F3EBFF;
        box-shadow: 0px 1px 6px 1px rgba(51,25,81,0.11);
        border-radius: 12px;
        border: 1px solid #FFFFFF;
        align-items: center;
        justify-content: center;
        position: relative;
        margin: 0;
        padding: 0;
        margin-left: 4px;
      }

      .${Consts.COMPARE_BOX} .${Consts.COMPARE_TIP_EL_CLASS} {
        position: absolute;
        top: -30px;
        left: 50%;
        transform: translateX(-50%);
        width: 100px;
        text-align: center;
        line-height: normal;
        border-radius: 4px;
        color: #fff !important;
        background: #000;
        padding: 4px;
        font-size: 12px;
        font-family: PingFangSC-Regular, PingFang SC;
        font-weight: 400;
        margin: 0;
      }

      .${Consts.COMPARE_BOX}.active {
        display:inline-flex !important;
      }

      ${Consts.CUSTOM_TAG} .${Consts.COMPARE_ICON} {
        width: 12px !important;
        height: 12px !important;
        position: absolute !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;
        margin:0 !important;
        padding:0 !important;
        background:transparent !important;
        cursor: pointer;
        user-select: none;
        -webkit-user-drag: none;
        -webkit-tap-highlight-color: transparent;
      }

      .${Consts.HIDE_EL_CLASS} {
        display: none !important;
      }
    `
    document.head.appendChild(styleEl)
  }

  private removeInjectCSS() {
    const styleElSelector = `[${Consts.CUSTOM_ATTR['INJECT_STYLE_EL']}=${Consts.CUSTOM_ATTR_VALUE}]`
    document.querySelectorAll(styleElSelector).forEach((el) => {
      el.remove()
    })
  }

  private checkIsBlockPage() {
    const config = this.getConfig()
    const isBlockPage = window.location.protocol.indexOf('http') === -1
    const isMatchBlockPage = commonUtils.checkUrlIsMatched(window.location.href, config.blockUrls)
    const isMatchEnablePage = commonUtils.checkUrlIsMatched(window.location.href, config.enableUrls)
    this.isBlockCurPage = isBlockPage || (isMatchBlockPage && !isMatchEnablePage)
  }

  private applyTranslationStyle(styleType: 'weak' | 'same' | 'strong') {
    this.translatableParagraphs.forEach((t) => {
      const translateEl = t.translateEl
      translateEl.classList.remove(Consts.WAVY_THEME_CLASS)
      translateEl.classList.remove(Consts.WEAK_THEME_CLASS)
      if (styleType === 'strong') {
        translateEl.classList.add(Consts.WAVY_THEME_CLASS)
      }
      if (styleType === 'weak') {
        translateEl.classList.add(Consts.WEAK_THEME_CLASS)
      }
    })
  }

  // 切换成仅译文模式
  private applyTranslationMode() {
    this.translatableParagraphs.forEach(({ translatableWrapperEls, commonParentEl }) => {
      // 在仅译文的情况下，把第一层级的math-line元素给隐藏掉,这样原文才会都隐藏掉
      // :scope 表示当前查询的根元素
      const firstLevelElements = commonParentEl.querySelectorAll(':scope > .math-inline')
      firstLevelElements.forEach((el) => {
        el.classList.add(Consts.HIDE_EL_CLASS)
      })

      translatableWrapperEls.forEach(({ textNodeWrapperEl }) => {
        textNodeWrapperEl.classList.add(Consts.HIDE_EL_CLASS)
      })
    })
  }

  private applyTranslationStyleV2(styleType: TranslateServiceType.StyleType) {
    this.translatableParagraphs.forEach((t) => {
      const translateEl = t.translateElWrapper
      translateEl.classList.remove(Consts.WAVY_THEME_CLASS)
      translateEl.classList.remove(Consts.WEAK_THEME_CLASS)
      translateEl.classList.remove(Consts.BACKGROUND_GREY_EL_CLASS)

      if (styleType === 'dualGrey') {
        t.translatableWrapperEls.forEach(({ textNodeWrapperEl }) => {
          textNodeWrapperEl.classList.remove(Consts.HIDE_EL_CLASS)
        })
        translateEl.classList.add(Consts.BACKGROUND_GREY_EL_CLASS)
      } else if (styleType === 'dualWhite') {
        t.translatableWrapperEls.forEach(({ textNodeWrapperEl }) => {
          textNodeWrapperEl.classList.remove(Consts.HIDE_EL_CLASS)
        })
        translateEl.classList.remove(Consts.BACKGROUND_GREY_EL_CLASS)
      }
    })
  }

  // 生成唯一选择器的方法
  private generateUniqueSelector(el: HTMLElement, index: number): string {
    // 使用多种方式生成稳定的选择器
    const text = el.textContent?.trim().substring(0, 50) || ''
    const tagName = el.tagName.toLowerCase()
    const className = el.className

    // 如果有ID，优先使用ID
    if (el.id) {
      return `#${el.id}`
    }

    // 使用文本内容 + 标签名 + 索引创建唯一标识
    const textHash = this.simpleHash(text)
    return `[data-translate-element="${textHash}-${tagName}-${index}"]`
  }

  // 简单的哈希函数
  private simpleHash(str: string): string {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36)
  }

  // 检查元素是否应该被翻译
  private shouldTranslateElement(el: HTMLElement): boolean {
    if (this.hasExcludeAttrAndParentHasExcludeAttr(el)) {
      return false
    }

    const child = el.firstElementChild as HTMLElement
    if (child && child.tagName === 'IMG') {
      return false
    }

    const text = el.textContent?.trim() ?? ''
    return text.length > 0
  }

  private addModelCompareElement(
    r: Type.Paragraph,
    useType: PostDataTypes.UseType
  ): HTMLElement | null {
    if (r.translateElWrapper.getRootNode() !== document) {
      return null
    }
    // 如果 r.translateElWrapper 已经存在 compareWrapper，则直接返回
    if (r.translateElWrapper.querySelector(`.${Consts.COMPARE_BOX}`)) {
      return r.translateElWrapper.querySelector(`.${Consts.COMPARE_BOX}`) as HTMLElement
    }
    const compareWrapper = document.createElement(Consts.CUSTOM_TAG)
    compareWrapper.classList.add(Consts.COMPARE_BOX)

    compareWrapper.classList.add(`${Consts.CUSTOM_PREFIX}-compare`)

    const tip = document.createElement('span')
    tip.innerText = '多模型翻译对比'
    tip.classList.add(Consts.COMPARE_TIP_EL_CLASS)
    tip.classList.add(Consts.HIDE_EL_CLASS)

    const compareImg = document.createElement('img')
    compareImg.src = Consts.COMPARE_IMG_NONE
    compareImg.classList.add(Consts.COMPARE_ICON)

    compareWrapper.appendChild(tip)
    compareWrapper.appendChild(compareImg)
    // r.translateElWrapper.appendChild(compareWrapper)

    compareImg.onmouseenter = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      tip.classList.remove(Consts.HIDE_EL_CLASS)
    }

    compareImg.onmouseleave = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      tip.classList.add(Consts.HIDE_EL_CLASS)
    }

    compareImg.onclick = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      uiController.compareModelClick(r, useType)
    }

    r.translateEl.addEventListener('mouseenter', (e) => {
      e.stopPropagation()
      e.preventDefault()

      r.translateElWrapper.appendChild(compareWrapper)
    })

    r.translateElWrapper.addEventListener('mouseleave', (e) => {
      e.stopPropagation()
      e.preventDefault()

      r.translateElWrapper.removeChild(compareWrapper)
    })

    return compareWrapper
  }

  // 新增方法：获取所有段落（包括视口内外的）
  private async getAllParagraphs(
    containers: HTMLElementArray,
    config: Config,
    updateRangeSelection?: () => void
  ): Promise<Array<Paragraph>> {
    // 不进行视口检查，直接获取所有可翻译元素
    const { wrapperEls } = this.createAllTranslatableWrapperEls(containers, config)
    let paragraphs = this.createParagraphs(wrapperEls)
    paragraphs = await this.processShouldTranslate(paragraphs)
    if (updateRangeSelection) {
      updateRangeSelection()
    }
    paragraphs = this.mergeParagraphs(paragraphs)
    paragraphs = this.processIsNeedNewLine(paragraphs)
    return paragraphs
  }

  private createAllTranslatableWrapperEls(
    containers: HTMLElementArray,
    config: Config
  ): { wrapperEls: Array<TranslatableWrapper> } {
    const wrapperEls: Array<TranslatableWrapper> = []
    const textNodes: Array<Text> = []

    // 生成 textNodes
    const fn = (container: HTMLElement) => {
      const treeWalk = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        (node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement
            // 会译也是用 shadow root 的方式插入的，要直接过滤掉，不然也会监听进行翻译
            if (el.id === envHelper.withExtensionPrefix('content-root')) {
              return NodeFilter.FILTER_REJECT
            }
            if (this.isMarkAttr(el, Consts.CUSTOM_ATTR['TRANSLATABLE_NODE_PARENT_EL'])) {
              return NodeFilter.FILTER_REJECT
            }
            if (this.isMarkAttr(el, Consts.CUSTOM_ATTR['EXCLUDE_EL'])) {
              return NodeFilter.FILTER_REJECT
            }
            if (el.tagName.startsWith(Consts.CUSTOM_PREFIX.toUpperCase())) {
              return NodeFilter.FILTER_REJECT
            }
            if (el.classList.contains('math-ignore')) {
              return NodeFilter.FILTER_REJECT
            }
            // 注释掉视口检查，让所有元素都被包含
            // if (el.classList.contains(TranslateHelperConsts.OUT_VIEWPORT_EL_CLASS)) {
            //   if (!this.isMarkAttr(el, Consts.INTERSECTION_OBSERVER_EL)) {
            //     outViewportEls.push(el)
            //   }
            //   return NodeFilter.FILTER_REJECT
            // }

            return NodeFilter.FILTER_SKIP
          }

          return NodeFilter.FILTER_ACCEPT
        }
      )
      let textNode = treeWalk.nextNode() as Text
      while (textNode) {
        const textContent = textNode.textContent?.trim()
        if (textContent) {
          textNodes.push(textNode)
        }
        textNode = treeWalk.nextNode() as Text
      }
    }

    containers.forEach((container) => {
      fn(container)
    })

    // 生成 wrapperEl 和 textNodeWrapperEls
    textNodes.forEach((textNode) => {
      const { textNodeWrapperEl, wrapperEl } = this.createTranslatableNodeWrapper(textNode)
      wrapperEls.push({
        wrapperEl,
        textNodeWrapperEl,
      })
    })

    return { wrapperEls }
  }
}
